import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import FinanceMenu from "../../finance/financeMenu";
import InfosAlertYearDisabled from "../../../core/common/alerts/infosAlertYearDisabled";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getFinancialStudentSummary } from "../../../api/studentPaiement";
import { getAllPaymentPlans } from "../../../api/paiementPlans";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { updateAutorisationsBatch } from "../../../api/notes";
import { Spin } from "antd";
import RestrictionByPlanModal from "./restrictionByPlanModal";
import { getPaymentSettingsByYear } from "../../../api/paiement_settings";
import withPermission from "../../../core/common/autorisations/withPermission";
import { formatFinancialSummaryStudent } from "../../finance/payment/financialSummary";

// Boutton protéger pour enregistrer les autorisations
const ProtectedBtnSaveAutorisation = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-primary"
      onClick={props.saveAutorisation}
    >
      Enregistrer
    </button>
  ),
  [
    buildPermission(Resource.NOTE_RESTRICTION, Action.CREATE),
    buildPermission(Resource.NOTE_RESTRICTION, Action.UPDATE),
    buildPermission(Resource.NOTE_RESTRICTION, Action.DELETE),
  ],
  "masquer",
);

// Boutton protéger pour modifier les autorisations
const ProtectedBtnUpdateAutorisation = withPermission(
  (props: any) => (
    <button
      className="btn btn-primary"
      onClick={props.openRestrictionModal}
      disabled={!props.selectedYear?.id}
    >
      Restriction/Plan
    </button>
  ),
  [
    buildPermission(Resource.NOTE_RESTRICTION, Action.CREATE),
    buildPermission(Resource.NOTE_RESTRICTION, Action.UPDATE),
    buildPermission(Resource.NOTE_RESTRICTION, Action.DELETE),
  ],
  "masquer",
);

const RestrictionNote = () => {
  const { selectedYear } = useAcademicYear();

  const [rawStudentPaymentData, setRawStudentPaymentData] = useState<any[]>([]);
  const [allPaymentPlans, setAllPaymentPlans] = useState<any[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [classSearchText, setClassSearchText] = useState<string>(""); // état de recherche pour les classes
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 24;
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  // stocker l'id de tous les étudiants dont l'autorisation de blocage à été modifier
  const [autorisationEdited, setAutorisationEdited] = useState<any[]>([]);
  const [onSubmitting, setOnSubmitting] = useState<boolean>(false);
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);

  const [paymentSettings, setPaymentSettings] = useState<any>();

  const togglePlan = (id: string) => {
    setSelectedPlans((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const toggleClass = (classe: string) => {
    setSelectedClasses((prev) =>
      prev.includes(classe)
        ? prev.filter((item) => item !== classe)
        : [...prev, classe],
    );
  };

  const computePayment = (allPaymentData: any[]) => {
    if (!allPaymentData || allPaymentData.length <= 0) return [];

    if (selectedPlans.length > 0) {
      return allPaymentData.map((studentData: any) => {
        const somme_attendue = studentData?.plan?.reduce(
          (acc: number, plan: any) => {
            if (
              !selectedPlans.some((planId) => plan?.PaymentPlan?.id === planId)
            ) {
              return acc;
            }

            // calculer mes rabais qui s'applique à ce plan
            const discountAmount = studentData.myDiscounts.reduce(
              (acc: number, discount: any) => {
                const matched = discount.repartitions.filter(
                  (rep: any) => rep?.student_repartition_id === plan?.id,
                );
                const total_amount = matched.reduce(
                  (acc: number, rep: any) => acc + rep?.repartition_amount,
                  0,
                );
                return acc + total_amount;
              },
              0,
            );

            return (
              acc + Number(plan?.PaymentPlan?.amount || 0) - discountAmount
            );
          },
          0,
        );

        const paiements_entrants = studentData?.plan?.some((plan: any) =>
          selectedPlans.some((planId) => plan?.PaymentPlan?.id === planId),
        )
          ? studentData.payments.reduce((acc: number, payment: any) => {
              const matchedPaymentPlans = studentData.plan.filter((plan: any) =>
                selectedPlans.some(
                  (planId) => plan?.PaymentPlan?.id === planId,
                ),
              );

              const montantReparti = (payment.repartitions || []).reduce(
                (sum: number, rep: any) =>
                  matchedPaymentPlans.some(
                    (plan: any) => plan.id === rep.payment_plan?.id,
                  )
                    ? sum + (rep.amount || 0)
                    : sum,
                0,
              );

              return acc + montantReparti;
            }, 0)
          : 0;

        const paiement_trop_recu =
          paiements_entrants < somme_attendue
            ? 0
            : paiements_entrants - somme_attendue;
        const non_paye =
          somme_attendue > paiements_entrants
            ? Math.abs(paiements_entrants - somme_attendue)
            : 0;
        const credit = paiements_entrants - somme_attendue;

        return {
          ...studentData,
          somme_attendue,
          paiements_entrants,
          paiement_trop_recu,
          non_paye,
          credit,
          reste: non_paye,
          statut: studentData?.Registrations[0]?.blocked_Note
            ? "bloque"
            : "autorise",
        };
      });
    }

    return allPaymentData.map((studentData: any) => {
      const somme_attendue = studentData.plan.reduce(
        (acc: number, plan: any) => {
          const dueDate = new Date(plan?.PaymentPlan?.due_date);
          const today = new Date();
          // calculer mes rabais qui s'applique à ce plan
          const discountAmount = studentData.myDiscounts.reduce(
            (acc: number, discount: any) => {
              const matched = discount.repartitions.filter(
                (rep: any) => rep?.student_repartition_id === plan?.id,
              );
              const total_amount = matched.reduce(
                (acc: number, rep: any) => acc + rep?.repartition_amount,
                0,
              );
              return acc + total_amount;
            },
            0,
          );

          return today >= dueDate
            ? acc + Number(plan?.PaymentPlan?.amount || 0) - discountAmount
            : acc;
        },
        0,
      );

      const paiements_entrants = studentData.payments.reduce(
        (acc: number, payment: any) => {
          const montantReparti = (payment.repartitions || []).reduce(
            (sum: number, rep: any) => sum + (rep.amount || 0),
            0,
          );
          return acc + montantReparti;
        },
        0,
      );

      const paiement_trop_recu =
        paiements_entrants < somme_attendue
          ? 0
          : paiements_entrants - somme_attendue;
      const non_paye =
        somme_attendue > paiements_entrants
          ? Math.abs(paiements_entrants - somme_attendue)
          : 0;
      const credit = paiements_entrants - somme_attendue;
      // console.log("note bloqué?: ",studentData)
      return {
        ...studentData,
        somme_attendue,
        paiements_entrants,
        paiement_trop_recu,
        non_paye,
        credit,
        reste: non_paye,
        statut: studentData?.Registrations[0]?.blocked_Note
          ? "bloque"
          : "autorise",
      };
    });
  };

  const computedRows = useMemo(() => {
    return computePayment(rawStudentPaymentData);
  }, [rawStudentPaymentData, selectedPlans]);

  const classOptions = useMemo(() => {
    const classes = computedRows
      .map((studentData: any) => studentData?.classe)
      .filter(Boolean);
    return Array.from(new Set(classes)).sort((a, b) =>
      String(a).localeCompare(String(b), "fr"),
    );
  }, [computedRows]);

  // Filtrer les classes basé sur le texte de recherche
  const filteredClassOptions = useMemo(() => {
    if (!classSearchText) return classOptions;
    return classOptions.filter((classe: string) =>
      String(classe)
        .toLowerCase()
        .includes(classSearchText.toLowerCase()),
    );
  }, [classOptions, classSearchText]);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return computedRows.filter((studentData: any) => {
      const matchPlan =
        selectedPlans.length === 0 ||
        studentData?.plan?.some((plan: any) =>
          selectedPlans.includes(plan?.PaymentPlan?.id || ""),
        );

      const matchClass =
        selectedClasses.length === 0 ||
        selectedClasses.includes(studentData?.classe || "");

      const matchSearch =
        query.length === 0 ||
        studentData?.utilisateur?.toLowerCase().includes(query) ||
        studentData?.matricule?.toLowerCase().includes(query) ||
        studentData?.classe?.toLowerCase().includes(query);

      return matchPlan && matchClass && matchSearch;
    });
  }, [computedRows, selectedPlans, selectedClasses, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(
    () =>
      filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredRows, currentPage],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPlans, selectedClasses, searchTerm, selectedYear?.id]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const fetchAllDataPaymentByYear = async () => {
    try {
      if (!selectedYear?.id) {
        setRawStudentPaymentData([]);
        return;
      }

      const permissionContext = buildPermissionContext(
        Resource.NOTE_RESTRICTION,
        Action.READ,
      );
      const response = await getFinancialStudentSummary(
        selectedYear.id,
        permissionContext,
      );
      const formattedData = (response?.data || []).map(
        formatFinancialSummaryStudent,
      );
      // console.log("FormattedData: ", formattedData);
      setRawStudentPaymentData(formattedData);
    } catch (error: any) {
      // console.log('Erreur lors de la recuperation des donnes de paiement: ',error)
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Une erreur s'est produite, reessayez plus tard.",
      );
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const applyStatusesLocally = (updatedStudents: any[] = []) => {
    const statusByStudent = new Map(
      updatedStudents.map((item: any) => [
        item.id_studentProfil,
        item.blocked_Note,
      ]),
    );
    setRawStudentPaymentData((students) =>
      students.map((student) => {
        if (!statusByStudent.has(student.id)) return student;
        const registrations = [...(student.Registrations || [])];
        if (registrations[0]) {
          registrations[0] = {
            ...registrations[0],
            blocked_Note: statusByStudent.get(student.id),
          };
        }
        return { ...student, Registrations: registrations };
      }),
    );
  };

  const fetchAllPaymentPlan = async () => {
    if (!selectedYear?.id) {
      setAllPaymentPlans([]);
      return;
    }
    try {
      const paymentPlans = await getAllPaymentPlans(
        selectedYear.id,
        buildPermissionContext(Resource.NOTE_RESTRICTION, Action.READ),
      );
      setAllPaymentPlans(paymentPlans.data || []);
    } catch (error: any) {
      // console.log('Erreur lors de la recuperation des plans de paiement: ',error)
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des plans de paiement.",
      );
    }
  };

  // fonction pour récupérer les paramêtres de paiement de l'année academique
  const fetchPaymentParameters = async () => {
    try {
      if (!selectedYear) return;
      const res = await getPaymentSettingsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.NOTE_RESTRICTION, Action.READ),
      );
      // console.log("Parameters: ", res.data);
      setPaymentSettings(res?.data[0]);
    } catch (error: any) {
      // console.log("Error fetching payment parameters:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchPaymentParameters();
    fetchAllPaymentPlan();
    setIsLoading(true);
    setHasLoaded(false);
    fetchAllDataPaymentByYear();
  }, [selectedYear]);

  const formatAmount = (amount: number) =>
    `${Number(amount || 0).toLocaleString("fr-FR")} ${paymentSettings?.currency || "FCFA"}`;

  const openRestrictionModal = () => {
    setShowRestrictionModal(true);
  };

  const closeRestrictionModal = () => {
    setShowRestrictionModal(false);
  };

  // fonction pour changer l'autorisation d'un étudiant (sans sélection de plan de paiement)
  const toggleAutorization = (id_student: string, autorisation: string) => {
    const studentAndAutorization = { id_student, autorisation };

    if (
      autorisationEdited.some((item: any) => item.id_student === id_student)
    ) {
      setAutorisationEdited(
        autorisationEdited.filter((item) => item.id_student !== id_student),
      );
    } else {
      setAutorisationEdited([...autorisationEdited, studentAndAutorization]);
    }
  };

  // fonction pour enregistrer les autorisations modifiées
  const saveAutorisation = async () => {
    if (autorisationEdited.length === 0) {
      toast.warning("Aucune autorisation à enregistrer.");
      return;
    }
    if (!selectedYear?.id) {
      toast.warning("Aucun année scolaire sélectionnée.");
      return;
    }
    setOnSubmitting(true);
    try {
      const response = await updateAutorisationsBatch(
        {
          id_academicYear: selectedYear.id,
          changes: autorisationEdited.map((item: any) => ({
            id_studentProfil: item.id_student,
            blocked_Note: item.autorisation === "bloque",
          })),
        },
        buildPermissionContext(Resource.NOTE_RESTRICTION, Action.UPDATE),
      );

      applyStatusesLocally(response?.data || []);
      setAutorisationEdited([]); // Réinitialiser les autorisations après l'enregistrement
      toast.success("Autorisation enregistrée avec succès.");
    } catch (error: any) {
      // console.log("Erreur lors de l'enregistrement des autorisations: ", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors de l'enregistrement des autorisations.",
      );
    } finally {
      setOnSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <FinanceMenu />
        </div>

        {!selectedYear?.statut && <InfosAlertYearDisabled />}

        <div className="bg-white p-3 border rounded-1 d-flex align-items-center justify-content-between flex-wrap mb-4 pb-0">
          <h4 className="mb-3">Restriction des Notes</h4>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            <div className="flex-grow-1" style={{ minWidth: "230px" }}>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="ti ti-search" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Rechercher par etudiant, matricule, classe..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="dropdown">
              <button
                className="btn btn-outline-light bg-white dropdown-toggle"
                data-bs-toggle="dropdown"
                style={{ minWidth: "200px" }}
              >
                <i className="ti ti-wallet me-2" />
                {selectedPlans.length > 0
                  ? `${selectedPlans.length} plan(s)`
                  : "Plans de paiement"}
              </button>

              <ul
                className="dropdown-menu p-2"
                onClick={(e) => e.stopPropagation()}
                style={{ minWidth: "240px" }}
              >
                {allPaymentPlans.map((plan: any) => (
                  <li key={plan.id} className="mb-2">
                    <label
                      className={`dropdown-item d-flex flex-column ${
                        selectedPlans.includes(plan.id)
                          ? "bg-light text-primary rounded-1"
                          : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked={selectedPlans.includes(plan.id)}
                          onChange={() => togglePlan(plan.id)}
                        />
                        <span>{plan.label}</span>
                      </div>
                      <div className="ms-4">
                        <small className="text-muted">
                          {Number(plan.amount || 0).toLocaleString("fr-FR")}{" "}
                          {paymentSettings?.currency || "FCFA"}
                        </small>
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            <div className="dropdown">
              <button
                className="btn btn-outline-light bg-white dropdown-toggle"
                data-bs-toggle="dropdown"
                style={{ minWidth: "200px" }}
              >
                <i className="ti ti-school me-2" />
                {selectedClasses.length > 0
                  ? `${selectedClasses.length} classe(s)`
                  : "Classes"}
              </button>

              <ul
                className="dropdown-menu p-2"
                onClick={(e) => e.stopPropagation()}
                style={{ minWidth: "220px", maxHeight: "300px", overflowY: "auto", overflowX: "hidden" }}
              >
                <li className="mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Rechercher une classe..."
                    value={classSearchText}
                    onChange={(e) => setClassSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </li>
                {filteredClassOptions.map((classe) => (
                  <li key={classe} className="mb-2">
                    <label
                      className={`dropdown-item d-flex align-items-center gap-2 ${
                        selectedClasses.includes(classe)
                          ? "bg-light text-primary rounded-1"
                          : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={selectedClasses.includes(classe)}
                        onChange={() => toggleClass(classe)}
                      />
                      <span>{classe}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            {/* bouton pour ouvrir le modal de restriction des notes par plan */}
            <ProtectedBtnUpdateAutorisation
              openRestrictionModal={openRestrictionModal}
              selectedYear={selectedYear}
            />
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between mb-3">
          <span className="text-muted">
            {filteredRows.length} etudiant(s) trouve(s)
          </span>
          {autorisationEdited.length > 0 &&
            (onSubmitting ? (
              <button disabled={true} className="btn btn-outline-primary">
                Enregistrement en cours...
              </button>
            ) : (
              <ProtectedBtnSaveAutorisation
                saveAutorisation={saveAutorisation}
              />
            ))}
        </div>

        <div className="position-relative">
          <div className="row">
            {isLoading && !hasLoaded && (
              <div className="col-12">
                <div className="alert alert-info mb-0">
                  <div className="text-center py-5">
                    <Spin size="large" />
                    <br />
                    Chargement des etudiants...
                  </div>
                </div>
              </div>
            )}

            {!isLoading && filteredRows.length === 0 && (
              <div className="col-12">
                <div className="alert alert-light border mb-0">
                  Aucun etudiant ne correspond aux filtres actuels.
                </div>
              </div>
            )}

            {!isLoading &&
              paginatedRows.map((student: any) => {
                const editedAuto = autorisationEdited.find(
                  (item: any) => item.id_student === student.id,
                );

                const isAuthorized = editedAuto
                  ? editedAuto.autorisation === "autorise"
                  : student.statut === "autorise";
                const isLate =
                  student.paiements_entrants < student.somme_attendue &&
                  student.credit < 0;
                const badgeClass = isAuthorized
                  ? "badge-soft-success"
                  : "badge-soft-danger";
                const badgeLateClass = isLate
                  ? "badge-soft-danger"
                  : "badge-soft-success";
                const wrapperClass = isAuthorized
                  ? "bg-success-subtle border border-success"
                  : "bg-danger-subtle border border-danger";
                const textClass = isAuthorized ? "text-success" : "text-danger";
                const statusLabel = !isLate ? "A jour" : "En retard";
                const statusActionLabel = isAuthorized ? "Autorise" : "Bloque";
                const iconClass = isAuthorized
                  ? "ti ti-circle-check"
                  : "ti ti-alert-circle";

                return (
                  <div
                    key={student.id}
                    className="col-xxl-3 col-xl-4 col-md-6 d-flex"
                  >
                    <div className="card flex-fill">
                      <div className="card-header d-flex align-items-center justify-content-between p-3">
                        <span
                          className={`badge ${badgeLateClass} d-inline-flex align-items-center me-1`}
                        >
                          <i className="ti ti-circle-filled fs-5 me-1" />
                          {statusLabel}
                        </span>
                      </div>

                      <div className="card-body p-3">
                        <div
                          style={{ cursor: "pointer" }}
                          className={`${wrapperClass} rounded-2 p-2 mb-2 d-flex align-items-center justify-content-between`}
                          onClick={() =>
                            toggleAutorization(
                              student.id,
                              student.statut === "autorise"
                                ? "bloque"
                                : "autorise",
                            )
                          }
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className={`avatar avatar-lg flex-shrink-0 ${wrapperClass} d-flex align-items-center justify-content-center rounded-circle`}
                            >
                              <i className={`${iconClass} ${textClass} fs-1`} />
                            </div>
                            <div className="ms-2">
                              <h5 className={`mb-0 ${textClass}`}>
                                {student.utilisateur}
                              </h5>
                              <p className="mb-0 text-muted">
                                {student.matricule}
                              </p>
                              <small className="text-muted">
                                {student.classe || "Classe non definie"}
                              </small>
                            </div>
                          </div>

                          <span
                            className={`badge ${badgeClass} d-inline-flex align-items-center`}
                          >
                            <i className="ti ti-check fs-5 me-1" />
                            {statusActionLabel}
                          </span>
                        </div>

                        <div className="d-flex align-items-center justify-content-between gx-2">
                          <div>
                            <p className="mb-0">A payer</p>
                            <p className="text-dark mb-0">
                              {formatAmount(student.somme_attendue)}
                            </p>
                          </div>
                          <div>
                            <p className="mb-0">Solde</p>
                            <p className="text-dark mb-0">
                              {formatAmount(student.paiements_entrants)}
                            </p>
                          </div>
                          <div>
                            <p className="mb-0">Reste</p>
                            <p
                              className={`mb-0 fw-semibold ${
                                isLate ? "text-danger" : "text-success"
                              }`}
                            >
                              {formatAmount(student.reste)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          {!isLoading && totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                Précédent
              </button>
              <span className="text-muted">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
              >
                Suivant
              </button>
            </div>
          )}
        </div>

        <RestrictionByPlanModal
          show={showRestrictionModal}
          onClose={closeRestrictionModal}
          selectedYearId={selectedYear?.id}
          allPaymentPlans={allPaymentPlans}
          rawStudentPaymentData={rawStudentPaymentData}
          onApplied={applyStatusesLocally}
          currency={paymentSettings?.currency}
        />
      </div>
    </div>
  );
};

export default RestrictionNote;
